import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http, ApiError } from "../../shared/api/http.js";
import type { SessionUser } from "@smartvow/shared";

const SESSION_KEY = ["session"] as const;

/** Session courante (`null` si non connecté). */
export function useSession() {
  return useQuery<SessionUser | null>({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return (await http.get<{ user: SessionUser }>("/auth/me")).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) =>
      http.post<{ user: SessionUser }>("/auth/login", v),
    onSuccess: ({ user }) => qc.setQueryData(SESSION_KEY, user),
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { currentPassword: string; newPassword: string }) =>
      http.post<{ user: SessionUser }>("/auth/change-password", v),
    onSuccess: ({ user }) => qc.setQueryData(SESSION_KEY, user),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { token: string; password: string }) =>
      http.post<{ user: SessionUser }>("/auth/accept-invite", v),
    onSuccess: ({ user }) => qc.setQueryData(SESSION_KEY, user),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (v: { email: string }) => http.post<{ ok: true }>("/auth/forgot-password", v),
  });
}

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { token: string; password: string }) =>
      http.post<{ user: SessionUser }>("/auth/reset-password", v),
    onSuccess: ({ user }) => qc.setQueryData(SESSION_KEY, user),
  });
}

export function logout() {
  return http.post("/auth/logout").finally(() => {
    window.location.href = "/login";
  });
}
